import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  Box,
  Button,
  Modal,
  Typography,
  Stack,
  Paper,
  Grid,
  Divider,
  useTheme,
  alpha,
  Card,
  Container,
  Tooltip,
  IconButton,
  Backdrop,
  Fade,
  LinearProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { expensesReport } from 'components/PDF';
import { ButtonGroup } from 'components/Button';
import dayjs from 'dayjs';

// Icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import GetAppIcon from '@mui/icons-material/GetApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CircularProgress from '@mui/material/CircularProgress';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { CustomerForm } from 'components/Forms';
import DatePickerCard from 'components/Card/DatePickerCard';
import FinanceExcel from '../../components/Excel/FinanceExcel';
import AddpayoutForm from '../../components/Forms/AddpayoutForm';
import ExpensesHrApplyForm from '../../components/Forms/ExpensesHrApplyForm';
import { ExpensesTableApplove } from '../../components/Table';
import ExpensesApplyForm from '../../components/Forms/ExpensesApplyForm';
import ProjectPopup from '../Project/ProjectPopup';
import ExpensesDetail from './ExpensesDetail';
import { confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import { getExpenseApproverSlots } from '../../utils/functions/approverAccess';
import api from '../../utils/functions/api';
import * as actions from '../../redux/actions';
import { useSocket } from '../../contexts/SocketContext';
import { DevWinBadge } from '../../components/DevPageBadge';
import { useExpenseSortPref } from '../../hooks/useExpenseSortPref';
import { sortTodayExpenses } from '../../utils/functions/sortTodayExpenses';

const REJECTED_STATUSES = ['REJECT', 'REJECT_2', 'REJECT_3', 'CANCEL', 'HOLD'];
const PENDING_STATUSES = ['PENDING', 'APPROVE_1', 'APPROVE_2'];

// sub-tabs ของแท็บ "วันนี้" — เกณฑ์วันคนละแบบกัน (ดู fetchExpenses):
//  approved = Expense.date (วันขอเบิก) = วันนี้        (พฤติกรรมเดิมของแท็บ)
//  due      = max(วันอนุมัติ 3 จุด) = เมื่อวาน → ครบกำหนดจ่ายวันนี้ (status APPROVE/PREPARE/SUCCESS)
//  urgent   = is_urgent:true; ยังไม่จบเอาหมด + SUCCESS เอาเฉพาะที่จ่ายวันนี้
const TODAY_SUBTABS = [
  {
    key: 'approved',
    label: 'อนุมัติวันนี้',
    icon: CheckCircleRoundedIcon,
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  {
    key: 'due',
    label: 'จ่ายเงินวันนี้',
    icon: PaymentsRoundedIcon,
    gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
  },
  {
    key: 'urgent',
    label: 'ด่วน',
    icon: BoltRoundedIcon,
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  },
];

// ช่วงเวลาของ "วัน" ตามเวลาไทย (Asia/Bangkok) — offsetDays: 0 = วันนี้, -1 = เมื่อวาน — คืน { start, end } เป็น Date
const thaiDayBounds = (offsetDays = 0) => {
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
  const start = new Date(`${todayStr}T00:00:00.000+07:00`);
  start.setDate(start.getDate() + offsetDays);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
  return { start, end };
};

// ชุด chip กรองสถานะของแท็บนี้ — ไม่จำกัด status ที่ backend เลย (today = แค่กรองด้วยวันที่ ดู fetchExpenses
// ด้านล่าง ไม่ส่ง tab/status ไปเลย) เจอได้ทุก status จริงรวม REJECT/REJECT_2/REJECT_3/CANCEL ด้วย เลยมี
// Rejected/Cancelled เหมือน tab "ทั้งหมด" (REJECT/REJECT_2/REJECT_3 รวมเป็น chip เดียว "Rejected")
const STATUS_FILTER_OPTIONS = [
  { key: 'ALL', label: 'ทั้งหมด', color: 'default', statuses: [] },
  { key: 'PENDING', label: 'Pending', color: 'warning', statuses: ['PENDING', 'PREAPPROVE', 'APPROVE_1', 'APPROVE_2'] },
  { key: 'APPROVE', label: 'Approved', color: 'warning', statuses: ['APPROVE'] },
  { key: 'PREPARE', label: 'Prepared', color: 'info', statuses: ['PREPARE'] },
  { key: 'SUCCESS', label: 'Success', color: 'success', statuses: ['SUCCESS'] },
  { key: 'REJECTED', label: 'Rejected', color: 'error', statuses: ['REJECT', 'REJECT_2', 'REJECT_3'] },
  { key: 'CANCEL', label: 'Cancelled', color: 'secondary', statuses: ['CANCEL'] },
];

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: '80%', md: '60%' },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  maxHeight: '90vh',
  overflow: 'auto',
};

function ExpensesTabToday({ title, subtitle }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();

  const expenses = useSelector((state) => state.expenses);
  const projects = useSelector((state) => state.project);
  const customer = useSelector((state) => state.customer);
  const customerType = useSelector((state) => state.customerType);
  const me = useSelector((state) => state.me);
  const info = useSelector((state) => state.info);
  const budget = useSelector((state) => state.budget);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [paidType, setPaidType] = useState('TRANSFER');
  const [projectSelect, setProjectSelect] = useState('');
  const [budgetSelect, setBudgetSelect] = useState('');
  const [openPay, setOpenPay] = useState(false);
  const [datePay, setDatePay] = useState(new Date());
  const [exSelect, setExSelect] = useState('');
  const [slipImage, setSlipImage] = useState([]);
  const [bank, setBank] = useState();
  const [findProject, setFindProject] = useState('');
  const [search, setSearch] = useState('');
  const [dateStart, setDateStart] = useState([dayjs().toDate(), dayjs().toDate()]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [customerSubmitting, setCustomerSubmitting] = useState(false);
  const [customerSelect, setCustomerSelect] = useState('');
  // เรียงผลลัพธ์ — ปุ่ม "เรียงตาม" render อยู่ใน ExpensesTableApplove.js (ใช้ร่วมกันทุก tab) แต่ state จริง
  // เก็บที่นี่ — ต่างจาก 4 tab อื่น (backlog/future/archive/all) ตรงที่ default เป็น "status" ไม่ใช่
  // "createdAt" เพราะ tab นี้จัดกลุ่มตามสถานะ (ดู sortedFilter ด้านล่าง) เป็นพฤติกรรม default เดิมอยู่แล้ว
  // ก่อนมีปุ่มนี้ ต้องคงไว้ไม่งั้น divider หัวกลุ่มสถานะใน ExpensesTableApplove.js จะกระจายซ้ำเพราะข้อมูล
  // ไม่ต่อเนื่องกันตาม status อีกต่อไป (default เดิมของ tab นี้คือ group ตาม status เสมอ)
  // sortDir default ต้องเป็น 'asc' (ไม่ใช่ 'desc') ถึงจะได้ลำดับ Pending group ก่อนเหมือน default เดิมจริงๆ
  // — ก่อนแก้ sortedFilter ด้านล่างให้รับรู้ sortDir (เดิม groupDiff ไม่สนใจ sortDir เลย เป็นบั๊ก) ตอนนั้น
  // default 'desc' บังเอิญไม่มีผลอะไรเพราะโค้ดไม่ได้ใช้ค่านี้จริง พอแก้ไขให้ sortDir มีผลจริงแล้ว ต้องเปลี่ยน
  // default ตรงนี้ตามด้วย ไม่งั้นหน้า "วันนี้" จะเปิดมาเจอ Success อยู่บนสุดแทนที่จะเป็น Pending เหมือนเดิม
  // (default อยู่ที่ EXPENSE_SORT_DEFAULTS ใน useExpenseSortPref.js — ค่าที่เลือกใช้ร่วมกับแท็บวันนี้ของ Profile)
  const { sortBy, sortDir, setSort } = useExpenseSortPref('today');
  const [openPrepareModal, setOpenPrepareModal] = useState(false);
  const [popupProjectId, setPopupProjectId] = useState(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [openDetailPageModal, setOpenDetailPageModal] = useState(false);
  const [detailPageId, setDetailPageId] = useState(null);
  const [filterVat, setFilterVat] = useState(false);
  const [filterWithholding, setFilterWithholding] = useState(false);
  // เลือกได้หลาย chip พร้อมกัน (multi-select) — array ว่าง = "ทั้งหมด" ไม่กรอง
  const [statusFilters, setStatusFilters] = useState([]);
  // sub-tab ในแท็บ "วันนี้" (ดู TODAY_SUBTABS) — default = พฤติกรรมเดิม (Expense.date = วันนี้)
  const [todaySubTab, setTodaySubTab] = useState('approved');
  const toggleStatusFilter = (key) => {
    if (key === 'ALL') { setStatusFilters([]); return; }
    setStatusFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleOpenDetailModal = (data) => {
    setDetailData(data);
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setDetailData(null);
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm();

  const {
    control: customerControl,
    formState: { errors: customerErrors },
    handleSubmit: handleCustomerSubmit,
    reset: resetCustomer,
    setValue: setCustomerValue,
  } = useForm();

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenCustomerModal = () => setOpenCustomerModal(true);
  const handleCloseCustomerModal = () => {
    setOpenCustomerModal(false);
    resetCustomer();
  };

  const handleOpenPrepareModal = () => setOpenPrepareModal(true);
  const handleClosePrepareModal = () => setOpenPrepareModal(false);

  const handleExport = () => {
    const { exportToExcel } = FinanceExcel({
      data: expenses?.rows || [],
      fileName: 'expenses_report',
    });
    exportToExcel();
  };

  const handleOpenPay = (e) => {
    setOpenPay(true);
    setExSelect(e);
    const net =
      e?.net_price != null
        ? Number(e.net_price)
        : (Number(e?.price) || 0) - (Number(e?.withholding_tax) || 0);
    setValue('price', net > 0 ? net : '');
  };

  const handleClosePay = () => setOpenPay(false);

  const PENDING_STATUSES = 'PENDING,PREAPPROVE,APPROVE_1,APPROVE_2';

  const fetchExpenses = useCallback(async () => {
    setIsRefreshing(true);
    const common = { search, findProject: findProject?._id, sortBy, sortDir };
    let params;
    if (todaySubTab === 'due') {
      // ครบกำหนดจ่ายวันนี้ — อิง Expenses.expected_payment_date ตรงๆ (คอลัมน์ "จ่ายเงิน" ในตารางเป๊ะ)
      // แทน _readyAt เดิม (ของเดิมมีบั๊ก: ลบ Payment แล้ว _readyAt ขยับมาเป็น "วันนี้" ทำให้รายการหลุด
      // ไปอยู่ tab "ค้างดำเนินการ" แทน ดู dueToday ใน expenses.service.js createPipeline())
      //  - APPROVE/PREPARE: expected_payment_date <= วันนี้ (ครบกำหนดแล้ว ค้างจากวันก่อนก็นับ)
      //  - SUCCESS: expected_payment_date = วันนี้พอดีเท่านั้น
      params = { ...common, dueToday: 'true' };
    } else if (todaySubTab === 'urgent') {
      const t = thaiDayBounds(0);
      params = {
        ...common,
        urgentQueue: 'true',
        urgentSuccessStart: t.start,
        urgentSuccessEnd: t.end,
      };
    } else {
      const ds = dayjs(dateStart?.[0]);
      const de = dayjs(dateStart?.[1]);
      params = {
        ...common,
        dateStart: ds.isValid() ? ds.startOf('day').toDate() : undefined,
        dateEnd: de.isValid() ? de.endOf('day').toDate() : undefined,
      };
    }
    await dispatch(actions.expensesAll(params));
    setIsRefreshing(false);
  }, [dispatch, search, findProject, dateStart, sortBy, sortDir, todaySubTab]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // จำนวนรายการต่อ sub-tab (อนุมัติวันนี้/ด่วน/จ่ายเงินวันนี้) — ยิงแยกด้วย size=1 เอาแค่ total ไม่ทับ
  // redux state.expenses ที่ตารางใช้อยู่ (เกณฑ์วันของแต่ละ sub-tab คนละแบบ ดู fetchExpenses)
  const [subTabCounts, setSubTabCounts] = useState({});
  const fetchSubTabCounts = useCallback(async () => {
    const buildQuery = (key) => {
      const q = new URLSearchParams({ size: '1', page: '1' });
      if (key === 'due') {
        q.set('dueToday', 'true');
      } else if (key === 'urgent') {
        const t = thaiDayBounds(0);
        q.set('urgentQueue', 'true');
        q.set('urgentSuccessStart', t.start.toISOString());
        q.set('urgentSuccessEnd', t.end.toISOString());
      } else {
        const ds = dayjs(dateStart?.[0]);
        const de = dayjs(dateStart?.[1]);
        if (ds.isValid()) q.set('dateStart', ds.startOf('day').toDate().toISOString());
        if (de.isValid()) q.set('dateEnd', de.endOf('day').toDate().toISOString());
      }
      return q.toString();
    };
    const entries = await Promise.all(
      TODAY_SUBTABS.map(async (t) => {
        try {
          const { data } = await api.get(
            `${process.env.REACT_APP_API_URL}/expenses?${buildQuery(t.key)}`,
          );
          return [t.key, data?.total ?? 0];
        } catch (error) {
          console.error(error);
          return [t.key, null];
        }
      }),
    );
    setSubTabCounts(Object.fromEntries(entries));
  }, [dateStart]);

  useEffect(() => {
    fetchSubTabCounts();
  }, [fetchSubTabCounts]);

  // Socket.IO real-time listeners
  const socket = useSocket();
  useEffect(() => {
    if (!socket) return;
    const refresh = () => {
      fetchExpenses();
      fetchSubTabCounts();
    };

    socket.on('expenses:created', refresh);
    socket.on('expenses:updated', refresh);
    socket.on('expenses:deleted', refresh);
    socket.on('payout:created', refresh);

    return () => {
      socket.off('expenses:created', refresh);
      socket.off('expenses:updated', refresh);
      socket.off('expenses:deleted', refresh);
      socket.off('payout:created', refresh);
    };
  }, [socket, page, size, search, findProject, dateStart, statusFilters, todaySubTab, fetchSubTabCounts]);

  useEffect(() => {
    dispatch(actions.expensesReset());
    dispatch(actions.projectAll({ lite: true }));
    dispatch(actions.customerAll({}));
    dispatch(actions.meGet());
    dispatch(actions.getInformation({}));
    dispatch(actions.customerTypeAll({}));
    return () => {};
  }, []);

  useEffect(() => {
    if (projectSelect) {
      dispatch(actions.budgetAll({ project_id: projectSelect?._id }));
    }
    return () => {};
  }, [projectSelect]);

  const filter = useMemo(() => (expenses?.rows || []).filter((row) => {
    if (!filterVat && !filterWithholding) return true;
    if (filterVat && filterWithholding) return row?.includes_vat || row?.withholding_tax > 0;
    if (filterVat) return !!row?.includes_vat;
    if (filterWithholding) return row?.withholding_tax > 0;
    return true;
  }), [expenses?.rows, filterVat, filterWithholding]);

  // sortBy 'status' (default เดิม) ยังจัดกลุ่มตาม STATUS_GROUP_ORDER เหมือนเดิมเป๊ะ — ตัวเลือกอื่นจากปุ่ม
  // "เรียงตาม" (createdAt/date/amount, asc/desc) เรียงจากฟิลด์นั้นๆ ตรงๆ แทน (ไม่กรุ๊ปตามสถานะแล้ว เป็นไปตามที่ user เลือก)
  const sortedFilter = useMemo(() => {
    const activeStatusValues = STATUS_FILTER_OPTIONS
      .filter((o) => o.key !== 'ALL' && statusFilters.includes(o.key))
      .flatMap((o) => o.statuses);
    const statusFiltered = filter.filter((row) => (
      activeStatusValues.length === 0 || activeStatusValues.includes(row.status)
    ));
    // ฟังก์ชันเรียงใช้ร่วมกับแท็บวันนี้ของ Profile (expenseQueue) — แก้ที่ sortTodayExpenses.js ที่เดียว
    return sortTodayExpenses(statusFiltered, sortBy, sortDir);
  }, [filter, statusFilters, sortBy, sortDir]);

  const handleDelete = useCallback(async (id) => {
    const confirm = await confirmAsync('ลบข้อมูล');
    if (confirm) {
      try {
        await dispatch(actions.expensesPut(id, {
          status: 'CANCEL',
          approval_actor: me?.userData?._id,
          approval_actor_name: formatEmployeeThaiName(me?.userData),
        }));
        fetchExpenses();
      } catch (error) {}
    }
  }, [dispatch, fetchExpenses, me]);

  const handleEdit = useCallback((id) => {
    history.push(`/finance/expenses/edit/${id}`);
  }, [history]);

  const handleDetail = useCallback((id) => {
    setDetailPageId(id);
    setOpenDetailPageModal(true);
  }, []);

  const handlePreApprove = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'PREAPPROVE',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleApprove1 = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'APPROVE_1',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleApprove2 = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'APPROVE_2',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleApprove3 = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'APPROVE',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  // อนุมัติแบบ 1 step — เก็บชื่อเป็น "ผู้มีสิทธิ์อนุมัติคนที่ 1" (info.expense_approver_1) เสมอ
  // แทนชื่อคนที่กดจริง (approval_actor id ยังเป็นคนที่กดจริงเหมือนเดิม เปลี่ยนแค่ชื่อที่โชว์)
  const handleApprove = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'APPROVE',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(info?.expense_approver_1) || formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleSuccess = async (id) => {
    await dispatch(actions.expensesPut(id, { status: 'SUCCESS' }));
    fetchExpenses();
  };

  const handleReject1 = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'REJECT',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleReject2 = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'REJECT_2',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleReject3 = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'REJECT_3',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleReject = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'REJECT',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };

  const handleHold = async (id) => {
    await dispatch(actions.expensesPut(id, { status: 'HOLD' }));
    fetchExpenses();
  };

  const handleBillPickup = async (id) => {
    await dispatch(actions.expensesPut(id, { bill_pickup: true }));
    fetchExpenses();
  };

  const handleReset = async (id) => {
    await dispatch(
      actions.expensesPut(id, {
        status: 'PENDING',
        approval_actor: me?.userData?.id,
        approval_actor_name: formatEmployeeThaiName(me?.userData),
      }),
    );
    fetchExpenses();
  };
  const handlePrepareStatus = async (id) => {
    await dispatch(actions.expensesPut(id, { status: 'PREPARE' }));
    fetchExpenses();
  };

  const handlePrint = async () => {
    // ส่งเฉพาะรายการที่ไม่รวม Reject/Cancel/Hold เข้า PDF
    expensesReport(
      { ...expenses, rows: filteredExpenses },
      { ...info, date: dateStart[0] },
    );
  };
  const onSubmitCustomer = async (data) => {
    if (customerSubmitting) return;
    setCustomerSubmitting(true);
    try {
      data.bank = {
        bank_name: bank,
        account_name: data?.bank?.account_name,
        account_number: data?.bank?.account_number,
      };
      await dispatch(actions.customerCreate(data));
      await dispatch(actions.customerAll({}));
      handleCloseCustomerModal();
    } catch (error) {
      // handle error
    } finally {
      setCustomerSubmitting(false);
    }
  };
  const handleAddCustomerFromPayment = () => {
    handleOpenCustomerModal();
  };

  const onSubmit = async (data) => {
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (confirm) {
      const dataSubmit = {
        ...data,
        paidType,
        date: dayjs(date).endOf('day').toISOString(),
        employee: me?.userData?._id || undefined,
        employee_data: me?.userData,
        budget: budgetSelect?._id || undefined,
        project: projectSelect?._id || projectSelect || undefined,
        customer: customerSelect || undefined,
      };
      await dispatch(actions.expensesCreateWithOutNotify(dataSubmit));
      fetchExpenses();
      reset();
      handleClose();
    }
  };

  const onSubmitPay = async (data) => {
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (confirm) {
      const dataSubmit = {
        ...data,
        paidType,
        date: datePay,
        employee: me?.userData?._id,
        expenses: exSelect?.id,
        budget: exSelect?.budget?._id,
        slipImage,
      };
      await dispatch(actions.payoutCreate(dataSubmit));
      await dispatch(actions.payoutAll({}));
      await dispatch(actions.expensesPut(exSelect?._id, { status: 'SUCCESS' }));
      fetchExpenses();
      reset();
      setSlipImage([]);
      handleClosePay();
    }
  };

  const handleCreatePayment = async () => {
    await dispatch(actions.paymentCreateWithExpenses({
      date,
      creater: me?.userData?._id,
      create_by: formatEmployeeThaiName(me?.userData),
    }));
    history.push('/finance/payment');
    handleClosePrepareModal();
  };

  const handleClick = () => {
    if (selectedIndex === 0) {
      handleOpen();
    }
    if (selectedIndex === 1) {
      handlePrint();
    }
    if (selectedIndex === 2) {
      handleExport();
    }
  };

  // คำนวณข้อมูลสรุป (ไม่รวมรายการที่ Reject, Cancel, Hold) — memoized
  const summary = useMemo(() => {
    const active = filter.filter((item) => !REJECTED_STATUSES.includes(item?.status));
    return active.reduce((acc, item) => {
      acc.totalCount += 1;
      acc.totalAmount += item?.net_price || 0;
      if (PENDING_STATUSES.includes(item.status)) {
        acc.pendingCount += 1;
        acc.pendingAmount += item?.net || 0;
      } else if (item.status === 'APPROVE') {
        acc.approvedCount += 1;
        acc.approvedAmount += item?.net || 0;
      } else if (item.status === 'SUCCESS') {
        acc.successCount += 1;
        acc.successAmount += item?.net || 0;
      }
      return acc;
    }, { totalCount: 0, totalAmount: 0, pendingCount: 0, pendingAmount: 0, approvedCount: 0, approvedAmount: 0, successCount: 0, successAmount: 0 });
  }, [filter]);
  const { totalCount, totalAmount, pendingCount, pendingAmount, approvedCount, approvedAmount, successCount, successAmount } = summary;

  const renderModalAddPayment = () => (
    <Modal
      open={openPay}
      onClose={handleClosePay}
      aria-labelledby="modal-payment-title"
      closeAfterTransition
    >
      <Fade in={openPay}>
        <Box sx={style}>
          <Typography variant="h6" component="h2" className="mb-4">
            เพิ่มข้อมูลการชำระเงิน
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <form onSubmit={handleSubmit(onSubmitPay)}>
            <AddpayoutForm
              control={control}
              Controller={Controller}
              date={datePay}
              setDate={setDatePay}
              paidType={paidType}
              expenseData={exSelect}
              slipImage={slipImage}
              setSlipImage={setSlipImage}
            />
          </form>
        </Box>
      </Fade>
    </Modal>
  );

  const renderPrepareModal = () => (
    <Modal
      open={openPrepareModal}
      onClose={handleClosePrepareModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h5"
            component="h2"
            sx={{ fontWeight: 600 }}
          >
            สร้างรายการจ่ายเงิน
          </Typography>
        </Box>

        {/* Content */}
        <Box
          id="modal-modal-description"
          sx={{
            p: 3,
            overflowY: 'auto',
            flex: 1,
          }}
        >
          <DatePickerCard setDate={setDate} date={date} />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            bgcolor: 'grey.50',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClosePrepareModal}
            sx={{ minWidth: 100 }}
          >
            ยกเลิก
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ minWidth: 100 }}
            onClick={handleCreatePayment}
          >
            สร้างรายการ
          </Button>
        </Box>
      </Box>
    </Modal>
  );

  const renderModal = () => (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-expense-title"
      closeAfterTransition
    >
      <Fade in={open}>
        <Box sx={style}>
          <Typography variant="h6" component="h2" className="mb-4">
            เพิ่มรายการเบิกจ่าย
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <form onSubmit={handleSubmit(onSubmit)}>
            <ExpensesApplyForm
              control={control}
              Controller={Controller}
              date={date}
              setDate={setDate}
              paidType={paidType}
              setPaidType={setPaidType}
              projects={projects}
              setProjectSelect={setProjectSelect}
              projectSelect={projectSelect}
              setValue={setValue}
              budgetSelect={budgetSelect}
              setBudgetSelect={setBudgetSelect}
              budgets={budget}
              bank={bank}
              setBank={setBank}
              customer={customer}
              onAddCustomer={handleAddCustomerFromPayment}
              setCustomerSelect={setCustomerSelect}
              watch={watch}
            />
          </form>
        </Box>
      </Fade>
    </Modal>
  );

  const renderApprovalsInfo = () => {
    const approvalMode = info?.expense_approval_mode || 'THREE_STEP';
    const isOneStep = approvalMode === 'ONE_STEP';

    return (
      <Box
        sx={{
          mb: 2,
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette.info.main, 0.05),
          borderRadius: 1.5,
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        }}
      >
        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
          <span
            style={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            รูปแบบการอนุมัติ:
          </span>{' '}
          <span>
            {isOneStep ? '1 ขั้นตอน (อนุมัติครั้งเดียว)' : '3 ขั้น (ไล่ระดับ)'}
          </span>
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.85rem', mt: 0.5 }}
        >
          <span
            style={{ fontWeight: 600, color: theme.palette.text.secondary }}
          >
            ผู้อนุมัติ:
          </span>{' '}
          {isOneStep ? (
            <span>
              {/* ONE_STEP = ใครก็ได้ในรายการ โชว์ครบทุกคนที่ตั้งไว้ (expense_approver_1..10) */}
              {getExpenseApproverSlots(info).filter(Boolean).map((a) => formatEmployeeThaiName(a)).join(', ')}
            </span>
          ) : (
            <span>
              ({formatEmployeeThaiName(info?.expense_approver_1)} →{' '}
              {formatEmployeeThaiName(info?.expense_approver_2)} →{' '}
              {formatEmployeeThaiName(info?.expense_approver_3)})
            </span>
          )}
        </Typography>
      </Box>
    );
  };

  const renderSubTabs = () => (
    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 0.75,
          p: 0.75,
          borderRadius: '18px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: (t) =>
            t.palette.mode === 'dark'
              ? '0 4px 20px rgba(0,0,0,0.35)'
              : '0 4px 20px rgba(15,23,42,0.06)',
        }}
      >
        {TODAY_SUBTABS.map((t) => {
          const selected = todaySubTab === t.key;
          const Icon = t.icon;
          return (
            <Box
              key={t.key}
              onClick={() => { setTodaySubTab(t.key); setPage(1); }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.9,
                px: 2,
                py: 1,
                borderRadius: '14px',
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                transition:
                  'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                transform: selected ? 'translateY(-1px)' : 'none',
                background: selected ? t.gradient : 'transparent',
                boxShadow: selected
                  ? '0 6px 16px -4px rgba(0,0,0,0.35)'
                  : 'none',
                '&:hover': {
                  background: selected
                    ? t.gradient
                    : (th) =>
                        th.palette.mode === 'dark'
                          ? 'rgba(255,255,255,0.06)'
                          : 'rgba(15,23,42,0.04)',
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 19,
                  color: selected ? '#fff' : 'text.secondary',
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: selected ? 700 : 500,
                  color: selected ? '#fff' : 'text.secondary',
                }}
              >
                {t.label}
              </Typography>
              {typeof subTabCounts[t.key] === 'number' && (
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    px: 0.7,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '999px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    bgcolor: selected
                      ? 'rgba(255,255,255,0.28)'
                      : (th) =>
                          (th.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.12)'
                            : 'rgba(15,23,42,0.07)'),
                    color: selected ? '#fff' : 'text.secondary',
                  }}
                >
                  {subTabCounts[t.key]}
                </Box>
              )}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );

  const renderSummary = () => (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={6} md={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ReceiptLongIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  รายการทั้งหมด
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ lineHeight: 1.2 }}
                >
                  {totalCount}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.success.main, 0.06),
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: theme.palette.success.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CurrencyExchangeIcon
                  sx={{ color: 'white', fontSize: '1.2rem' }}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  ยอดรวม
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="success.main"
                  sx={{ lineHeight: 1.2 }}
                >
                  ฿
                  {totalAmount
                    .toFixed(2)
                    .replace(/\d(?=(\d{3})+(?!\d))/g, '$&,')}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.warning.main, 0.06),
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: theme.palette.warning.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrendingUpIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  รออนุมัติ
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="warning.main"
                  sx={{ lineHeight: 1.2 }}
                >
                  ฿
                  {pendingAmount
                    .toFixed(2)
                    .replace(/\d(?=(\d{3})+(?!\d))/g, '$&,')}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.06),
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: theme.palette.info.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <EventNoteIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.7rem' }}
                >
                  ช่วงวันที่
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{ lineHeight: 1.2, fontSize: '0.8rem' }}
                >
                  {dayjs(dateStart[0]).format('DD/MM')}-
                  {dayjs(dateStart[1]).format('DD/MM')}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );

  const renderTable = () => (
    <ExpensesTableApplove
      expenses={expenses}
      page={page}
      size={size}
      setPage={setPage}
      setSize={setSize}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      filter={sortedFilter}
      allFilter={filter}
      statusFilters={statusFilters}
      onStatusFilterToggle={toggleStatusFilter}
      statusOptions={STATUS_FILTER_OPTIONS}
      handlePreApprove={handlePreApprove}
      handleApprove={handleApprove}
      handleApprove1={handleApprove1}
      handleApprove2={handleApprove2}
      handleApprove3={handleApprove3}
      handleReject1={handleReject1}
      handleReject2={handleReject2}
      handleReject3={handleReject3}
      handleSuccess={handleSuccess}
      handleBillPickup={handleBillPickup}
      handleDetail={handleDetail}
      handleOpenPay={handleOpenPay}
      handleReject={handleReject}
      handleHold={handleHold}
      handleReset={handleReset}
      handleOpenPrepareModal={handleOpenPrepareModal}
      handlePrepareStatus={handlePrepareStatus}
      handleOpenDetailModal={handleOpenDetailModal}
      me={me}
      info={info}
      onDoubleClickProject={(pid) => setPopupProjectId(pid)}
      tabVariant="today"
      sortBy={sortBy}
      sortDir={sortDir}
      onSortChange={setSort}
    />
  );

  const options = ['CREATE EXPENSE', 'PRINT REPORT', 'EXPORT TO EXCEL'];

  const renderFiltersAndActions = () => (
    <Box sx={{ mb: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ display: { xs: 'flex', md: 'flex' }, flexShrink: 0 }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpen}
          sx={{ borderRadius: 1.5, whiteSpace: 'nowrap' }}
        >
          สร้างรายการ
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          sx={{ borderRadius: 1.5 }}
        >
          พิมพ์
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="success"
          startIcon={<GetAppIcon />}
          onClick={handleExport}
          sx={{ borderRadius: 1.5 }}
        >
          Excel
        </Button>
      </Stack>
    </Box>
  );

  const renderDetailModal = () => (
    <Modal
      open={openDetailModal}
      onClose={handleCloseDetailModal}
      aria-labelledby="modal-detail-title"
      closeAfterTransition
    >
      <Fade in={openDetailModal}>
        <Box sx={style}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h2" fontWeight="bold">
              รายละเอียดรายการเบิกจ่ายแบบย่อ
            </Typography>
            <IconButton onClick={handleCloseDetailModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 3 }} />
          {detailData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">รหัสเอกสาร</Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">{detailData?.code}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">วันที่ขอเบิก</Typography>
                <Typography variant="body1">{dayjs(detailData?.date).format('DD/MM/YYYY')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">โครงการ</Typography>
                <Typography variant="body1">{detailData?.project?.name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">งบประมาณ</Typography>
                <Typography variant="body1">
                  {detailData?.budget?.prefix}{detailData?.budget?.budget_number} {detailData?.budget?.name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">ผู้ขอเบิก</Typography>
                <Typography variant="body1">{formatEmployeeThaiName(detailData?.employee)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">ยอดรวม (บาท)</Typography>
                <Typography variant="h6" fontWeight="bold" color="error">
                  ฿{detailData?.net?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                </Typography>
              </Grid>
              {detailData?.includes_vat && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">VAT 7% (รวมในยอดรวม)</Typography>
                  <Typography variant="body1" fontWeight="bold" color="info.main">
                    ฿{detailData?.vat_amount?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </Typography>
                </Grid>
              )}
              {detailData?.withholding_tax ? (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">หัก ณ ที่จ่าย 3%</Typography>
                  <Typography variant="body1" fontWeight="bold" color="warning.main">
                    - ฿{detailData?.withholding_tax?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">ยอดจ่ายจริง</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  ฿{(detailData?.net_net ?? ((detailData?.net || 0) - (detailData?.withholding_tax || 0))).toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">รายการเบิก</Typography>
                <Typography variant="body1">{detailData?.name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">ผู้รับเงิน</Typography>
                <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1, mt: 0.5 }}>
                  <Typography variant="body2"><b>ชื่อ:</b> {detailData?.payee?.name || '-'}</Typography>
                  <Typography variant="body2"><b>ธนาคาร:</b> {detailData?.payee?.bank || '-'}</Typography>
                  <Typography variant="body2"><b>เลขบัญชี:</b> {detailData?.payee?.account_number || '-'}</Typography>
                </Box>
              </Grid>
              {/* {detailData?.budget && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>การใช้งบประมาณ</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ flexGrow: 1 }}>
                      ใช้งบไปแล้ว (ประเมินคร่าวๆ)
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(((detailData?.budget?.total_expenses || 0) + detailData?.net) / (detailData?.budget?.cost || 1) * 100, 100) || 0}
                    color={((detailData?.budget?.total_expenses || 0) + detailData?.net) / (detailData?.budget?.cost || 1) * 100 > 90 ? 'error' : 'primary'}
                    sx={{ height: 6, borderRadius: 3, mb: 1 }}
                  />
                  <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">งบตั้งต้น</Typography>
                      <Typography variant="body2" color="primary">฿{(detailData?.budget?.cost || 0).toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">ใช้ไปแล้ว</Typography>
                      <Typography variant="body2" color="error">฿{(detailData?.budget?.total_expenses || 0).toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="textSecondary">คงเหลือ</Typography>
                      <Typography variant="body2" color="success.main">฿{(detailData?.budget?.remain || 0).toLocaleString()}</Typography>
                    </Grid>
                  </Grid>
                </Grid>
              )} */}
            </Grid>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={handleCloseDetailModal}>
              ปิด
            </Button>
            <Button variant="contained" color="primary" sx={{ ml: 2 }} onClick={() => {
              const id = detailData?._id;
              handleCloseDetailModal();
              history.push(`/finance/expenses/detail/${id}`);
            }}>
              ดูรายละเอียดแบบเต็ม
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  const renderCustomerModal = () => (
    <Modal
      open={openCustomerModal}
      onClose={handleCloseCustomerModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={openCustomerModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: 700 },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">เพิ่มลูกค้าใหม่</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseCustomerModal}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, maxHeight: 'calc(90vh - 64px)', overflowY: 'auto' }}>
            <form onSubmit={handleCustomerSubmit(onSubmitCustomer)}>
              <CustomerForm
                Controller={Controller}
                control={customerControl}
                errors={customerErrors}
                customerType={customerType}
                bank={bank}
                setBank={setBank}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 3,
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseCustomerModal}
                  startIcon={<CloseIcon />}
                  disabled={customerSubmitting}
                >
                  ยกเลิก
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={customerSubmitting}
                  startIcon={customerSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                >
                  {customerSubmitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  const renderDetailPageModal = () => (
    <Modal
      open={openDetailPageModal}
      onClose={() => setOpenDetailPageModal(false)}
      closeAfterTransition
    >
      <Fade in={openDetailPageModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
            maxHeight: '95vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'primary.main',
              color: 'white',
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              รายละเอียดรายการเบิกจ่าย
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenDetailPageModal(false)}
              sx={{ color: 'white' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
            {detailPageId && (
              <ExpensesDetail
                propId={detailPageId}
                onClose={() => {
                  setOpenDetailPageModal(false);
                  fetchExpenses();
                }}
              />
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  const hasRows = !!(expenses?.rows?.length);

  return (
    <div>
      {expenses?.isLoading && hasRows && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: 2 }} />
      )}
      {/* {renderTitle()} */}
      <div className="flex justify-end">{renderFiltersAndActions()}</div>
      {renderSubTabs()}
      {renderSummary()}
      {renderApprovalsInfo()}
      {renderTable()}
      <DevWinBadge num={124} name="Expenses · Edit Expense" open={open} />
      {renderModal()}
      <DevWinBadge num={125} name="Expenses · Detail" open={openDetailModal} />
      {renderDetailModal()}
      <DevWinBadge num={126} name="Expenses · Full Detail Page" open={openDetailPageModal} />
      {renderDetailPageModal()}
      <DevWinBadge num={127} name="Expenses · Add Payment" open={openPay} />
      {renderModalAddPayment()}
      <DevWinBadge num={128} name="Expenses · Customer Select" open={openCustomerModal} />
      {renderCustomerModal()}
      <DevWinBadge num={129} name="Expenses · Prepare" open={openPrepareModal} />
      {renderPrepareModal()}
      <Dialog
        open={!!popupProjectId}
        onClose={() => setPopupProjectId(null)}
        maxWidth="xl"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{ sx: { mt: '40px', maxHeight: 'calc(100vh - 56px)', minWidth: '50vw' } }}
        hideBackdrop
      >
        <DialogContent sx={{ p: 0 }}>
          {popupProjectId && (
            <ProjectPopup
              propId={popupProjectId}
              onClose={() => setPopupProjectId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ExpensesTabToday;
